import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import SurveyEmbed from './SurveyEmbed';
import { getStore } from '../ApplicationState';
import { getUserProfileFromDB, isStudent } from '../UserManagementHelpers';
import { setUserProfile } from '../UserManagement';
import { setLocalStorage, getLocalStorage } from '../aaa';
import { createSurveySession } from '../BackendInterface';
import { ShowSurveyParameters } from '../../peekapak-types/SurveyCampaignTypes';
import { safeSessionStorage } from '../safeStorage';

// Session storage helpers for managing active survey sessions
interface StoredSurveySession {
  sessionId: string;
  surveyParameters: ShowSurveyParameters;
  createdAt: number;
}

const getActiveSession = (campaignId: string): StoredSurveySession | null => {
  if (!campaignId) return null;

  try {
    const stored = safeSessionStorage.getItem(`surveySession_${campaignId}`);
    if (stored) {
      const session: StoredSurveySession = JSON.parse(stored);
      // Check if session is still valid (24 hours)
      if (Date.now() - session.createdAt < 24 * 60 * 60 * 1000) {
        return session;
      }
      // Clean up expired session
      safeSessionStorage.removeItem(`surveySession_${campaignId}`);
    }
  } catch (error) {
    console.error('Error reading active session:', error);
  }
  return null;
};

const saveActiveSession = (campaignId: string, sessionId: string, surveyParameters: ShowSurveyParameters) => {
  if (!campaignId || !sessionId) return;

  try {
    const sessionData: StoredSurveySession = {
      sessionId,
      surveyParameters,
      createdAt: Date.now(),
    };
    safeSessionStorage.setItem(`surveySession_${campaignId}`, JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error saving active session:', error);
  }
};

const clearActiveSession = (campaignId: string) => {
  if (!campaignId) return;

  try {
    safeSessionStorage.removeItem(`surveySession_${campaignId}`);
  } catch (error) {
    console.error('Error clearing active session:', error);
  }
};

const TypeformPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isProcessingSubmission, setIsProcessingSubmission] = useState(false);
  const location = useLocation<{
    nextPathname?: string;
    surveyParameters?: ShowSurveyParameters;
  }>();
  const history = useHistory();

  const { userProfile } = getStore().getState().user;

  // Check for stored next survey parameters from a chain navigation
  const storedNextSurvey = (() => {
    try {
      const stored = safeSessionStorage.getItem('nextSurveyParameters');
      if (stored) {
        const params = JSON.parse(stored);
        // Clear it immediately after reading to prevent reuse
        safeSessionStorage.removeItem('nextSurveyParameters');
        return params;
      }
    } catch (error) {
      console.error('Error reading stored survey parameters:', error);
    }
    return null;
  })();

  // Get survey parameters from stored chain, location state, user profile, or active session
  const surveyParams: ShowSurveyParameters = (() => {
    // First priority: stored parameters from survey chain
    if (storedNextSurvey?.surveyParameters) {
      // Also update location state for consistency
      if (
        !location.state?.surveyParameters ||
        location.state.surveyParameters.campaignId !== storedNextSurvey.surveyParameters.campaignId
      ) {
        history.replace(location.pathname, {
          surveyParameters: storedNextSurvey.surveyParameters,
          nextPathname: storedNextSurvey.nextPathname,
        });
      }
      return storedNextSurvey.surveyParameters;
    }

    const locationCampaignId = location.state?.surveyParameters?.campaignId;
    const profileCampaignId = userProfile?.surveyParameters?.campaignId;

    if (locationCampaignId === profileCampaignId) {
      // Location state is current, use it (preserves navigation context)
      return location.state.surveyParameters;
    }

    // Check if we have an active session with survey parameters
    if (profileCampaignId) {
      const activeSession = getActiveSession(profileCampaignId);
      if (activeSession && activeSession.surveyParameters) {
        return activeSession.surveyParameters;
      }
    }

    // Location state is stale or missing, use profile
    return userProfile?.surveyParameters || ({} as ShowSurveyParameters);
  })();

  const typeformId = surveyParams.formId;

  // Get user profile to extract district and schoolId

  // Helper function to map grade to instruction level
  const getInstructionLevel = (grade: number): string => {
    if (grade === -1) return 'PreKindergarten';
    if (grade === 0) return 'Kindergarten';
    if (grade >= 1 && grade <= 12) {
      const suffix = grade === 1 ? 'st' : grade === 2 ? 'nd' : grade === 3 ? 'rd' : 'th';
      return `${grade}${suffix} Grade`;
    }
    if (grade === undefined || grade === null) {
      return 'NA';
    }
    return `Grade ${grade}`; // Fallback for unexpected grades
  };

  // Create survey session when component mounts
  useEffect(() => {
    const initializeSurveySession = async () => {
      try {
        setIsLoadingSession(true);
        setSessionError(null);

        // Check for existing session for this campaign
        const existingSession = getActiveSession(surveyParams.campaignId);

        if (existingSession) {
          console.log('Reusing existing session for campaign:', surveyParams.campaignId, existingSession.sessionId);
          setSessionId(existingSession.sessionId);
          return;
        }

        // Create new session only if none exists
        console.log('Creating new session for campaign:', surveyParams.campaignId);
        const response = await createSurveySession(surveyParams);
        setSessionId(response.sessionId);
        saveActiveSession(surveyParams.campaignId, response.sessionId, surveyParams);
      } catch (error) {
        console.error('Error creating survey session:', error);
        setSessionError('Failed to initialize survey session. Please try again.');
      } finally {
        setIsLoadingSession(false);
      }
    };

    if (surveyParams.campaignId && surveyParams.formId) {
      initializeSurveySession();
    } else {
      setIsLoadingSession(false);
    }
  }, [surveyParams.campaignId, surveyParams.formId]);

  const handleSubmit = async () => {
    setIsSubmitted(true);
    setIsProcessingSubmission(true);

    // Set a flag to prevent redirects during survey completion processing
    setLocalStorage('surveyCompletionInProgress', 'true', 1); // 1 minute expiry

    // Clear active session for this campaign since survey is completed
    const campaignId = surveyParams.campaignId;
    if (campaignId) {
      clearActiveSession(campaignId);

      // Temporary tracking until userProfile refreshes (24 hour expiry)
      try {
        const completedSurveys = JSON.parse(getLocalStorage('completedSurveys') || '[]');
        completedSurveys.push({ campaignId, completedAt: Date.now() });
        // Set with 24 hour expiry (24 * 60 = 1440 minutes)
        setLocalStorage('completedSurveys', JSON.stringify(completedSurveys), 1440);
      } catch (error) {
        console.error('Error saving survey completion to localStorage:', error);
      }
    }

    // Log the session that was submitted
    console.log(`Form submitted with session ID: ${sessionId}, campaign ID: ${campaignId}`);

    // Wait longer for backend to process the completion and update campaign status
    // This gives the backend time to mark the current campaign as complete
    // before we check for the next survey in the chain
    await new Promise((resolve) => setTimeout(resolve, 3000));

    // Refresh user profile to check for next survey
    try {
      const updatedProfile = await getUserProfileFromDB();
      getStore().dispatch(setUserProfile(updatedProfile));

      // Processing is done, now we're checking for the next survey
      setIsProcessingSubmission(false);

      // Debug logging
      console.log('Survey chain check:', {
        hasIsShowSurvey: updatedProfile?.flags?.isShowSurvey,
        hasSurveyParameters: !!updatedProfile?.surveyParameters,
        newCampaignId: updatedProfile?.surveyParameters?.campaignId,
        oldCampaignId: surveyParams.campaignId,
        isDifferentCampaign: updatedProfile?.surveyParameters?.campaignId !== surveyParams.campaignId,
      });

      // Check if another survey is available
      // Note: We check for different campaignId OR if backend explicitly wants a retry
      if (updatedProfile?.flags?.isShowSurvey && updatedProfile?.surveyParameters?.campaignId) {
        const isNewCampaign = updatedProfile.surveyParameters.campaignId !== surveyParams.campaignId;

        if (!isNewCampaign) {
          // Same campaign - backend might need more time to update
          console.log('Same campaign returned by backend, attempting one retry after additional delay');

          // Give backend more time to update and try once more
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Try one more time to get updated profile
          try {
            const retryProfile = await getUserProfileFromDB();
            getStore().dispatch(setUserProfile(retryProfile));

            // Check again if the campaign ID changed
            const retryIsNewCampaign =
              retryProfile?.surveyParameters?.campaignId &&
              retryProfile.surveyParameters.campaignId !== surveyParams.campaignId;

            if (retryIsNewCampaign && retryProfile?.flags?.isShowSurvey) {
              console.log('Next survey detected on retry:', retryProfile.surveyParameters.campaignId);
              setIsLoadingNext(true);

              // Clear the completed surveys for the new campaign
              try {
                const completedSurveys = JSON.parse(getLocalStorage('completedSurveys') || '[]');
                const filteredSurveys = completedSurveys.filter(
                  (s: any) => s.campaignId !== retryProfile.surveyParameters.campaignId,
                );
                setLocalStorage('completedSurveys', JSON.stringify(filteredSurveys), 1440);
              } catch (error) {
                console.error('Error updating completed surveys:', error);
              }

              // Navigate to survey choice for the next survey
              // Store the next survey parameters in sessionStorage before reload
              setTimeout(() => {
                // Clear the processing flag before navigation
                setLocalStorage('surveyCompletionInProgress', 'false', 1);
                safeSessionStorage.setItem(
                  'nextSurveyParameters',
                  JSON.stringify({
                    surveyParameters: retryProfile.surveyParameters,
                    nextPathname: location.state?.nextPathname || '/',
                  }),
                );
                // Navigate to survey choice so user can decide whether to continue
                window.location.href = '/survey-choice';
              }, 2000);
              return; // Exit early to avoid double navigation
            } else {
              console.log('Still same campaign after retry, redirecting to destination');
            }
          } catch (retryError) {
            console.error('Error on retry check for next survey:', retryError);
          }

          // No new survey found, redirect to original destination
          const nextPath = location.state?.nextPathname || '/';
          setTimeout(() => {
            // Clear the processing flag before navigation
            setLocalStorage('surveyCompletionInProgress', 'false', 1);
            history.push(nextPath);
          }, 3000);
        } else {
          // Another survey is available
          console.log('Next survey detected:', updatedProfile.surveyParameters.campaignId);
          setIsLoadingNext(true);

          // Clear the completed surveys for the new campaign to prevent blocking
          // Remove the new campaign from completed list if it's there
          try {
            const completedSurveys = JSON.parse(getLocalStorage('completedSurveys') || '[]');
            const filteredSurveys = completedSurveys.filter(
              (s: any) => s.campaignId !== updatedProfile.surveyParameters.campaignId,
            );
            setLocalStorage('completedSurveys', JSON.stringify(filteredSurveys), 1440);
          } catch (error) {
            console.error('Error updating completed surveys:', error);
          }

          // Navigate to survey choice for the next survey
          // Store the next survey parameters in sessionStorage before reload
          setTimeout(() => {
            // Clear the processing flag before navigation
            setLocalStorage('surveyCompletionInProgress', 'false', 1);
            safeSessionStorage.setItem(
              'nextSurveyParameters',
              JSON.stringify({
                surveyParameters: updatedProfile.surveyParameters,
                nextPathname: location.state?.nextPathname || '/',
              }),
            );
            // Navigate to survey choice so user can decide whether to continue
            window.location.href = '/survey-choice';
          }, 2000);
        }
      } else {
        // All surveys complete - redirect to original destination
        console.log('All surveys complete, redirecting to:', location.state?.nextPathname || '/');

        const nextPath = location.state?.nextPathname || '/';
        setTimeout(() => {
          // Clear the processing flag before navigation
          setLocalStorage('surveyCompletionInProgress', 'false', 1);
          history.push(nextPath);
        }, 3000);
      }
    } catch (error) {
      console.error('Error checking for next survey:', error);
      // Reset processing state on error
      setIsProcessingSubmission(false);
      // Clear the processing flag on error
      setLocalStorage('surveyCompletionInProgress', 'false', 1);
      // Fallback to original destination on error
      const nextPath = location.state?.nextPathname || '/';
      setTimeout(() => {
        history.push(nextPath);
      }, 3000);
    }
  };

  return (
    <div
      className='bg-gray-50 px-4 sm:px-6 lg:px-8 flex items-center justify-center'
      style={{ minHeight: 'calc(100vh - 80px)' }}
    >
      <div className='max-w-4xl w-full py-8'>
        {isSubmitted ? (
          <div className='bg-white shadow-lg rounded-lg p-8 text-center'>
            {isProcessingSubmission ? (
              <>
                <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto'></div>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Processing your submission...</h2>
                <p className='mt-2 text-gray-500'>Please wait while we save your responses.</p>
              </>
            ) : isLoadingNext ? (
              <>
                <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto'></div>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Great work!</h2>
                <p className='mt-2 text-gray-500'>Loading your next survey...</p>
              </>
            ) : (
              <>
                <svg
                  className='mx-auto text-green-600'
                  width='64'
                  height='64'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <circle cx='12' cy='12' r='11' fill='#D1FAE5' />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Thank you for your feedback!</h2>
                <p className='mt-2 text-gray-500'>Your responses have been recorded. We appreciate your time.</p>
              </>
            )}
          </div>
        ) : isLoadingSession ? (
          <div className='bg-white shadow-lg rounded-lg p-8 text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto'></div>
            <h2 className='mt-3 text-lg font-medium text-gray-900'>Preparing your survey...</h2>
            <p className='mt-2 text-gray-500'>Please wait while we set up your survey session.</p>
          </div>
        ) : sessionError ? (
          <div className='bg-white shadow-lg rounded-lg p-8 text-center'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100'>
              <svg className='h-6 w-6 text-red-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </div>
            <h2 className='mt-3 text-lg font-medium text-gray-900'>Survey Initialization Error</h2>
            <p className='mt-2 text-gray-500'>{sessionError}</p>
          </div>
        ) : (
          <div className='bg-white shadow-lg rounded-lg overflow-hidden'>
            <div className='px-4 py-5 sm:p-6'>
              <SurveyEmbed
                formId={typeformId}
                height='600px'
                onSubmit={handleSubmit}
                className='rounded-lg'
                hideHeaders={true}
                hideFooter={false}
                hiddenFields={{
                  session_id: sessionId,
                  campaign_id: surveyParams.campaignId || '',
                  district: userProfile?.district || '',
                  school_id: userProfile?.schoolId || '',
                  ...(userProfile &&
                    isStudent(userProfile) && {
                      instruction_level: getInstructionLevel(userProfile.grade),
                    }),
                  survey_domain: surveyParams.surveyDomain,
                  respondent_group: surveyParams.respondentGroup,
                  timestamp: Date.now().toString(),
                  source: 'embedded-form',
                  nationality: userProfile?.nationality || 'unknown',
                  ethnicity: userProfile?.ethnicity || 'unknown',
                  gender: userProfile?.gender || 'unknown',
                  birthdate: userProfile?.birthdate || 'unknown',
                  socio_economic_status: userProfile?.socioEconomicStatus || 'unknown',
                  iep: userProfile?.iep || 'unknown',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeformPage;
